import { useMemo, useState } from "react";
import {
  Box, IconButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { buildCauseGroups, getCauseMachineName } from "./groupedCauseRows";
import "./groupedCauseTable.css";

const columns = [
  { key: "machine", label: "설비", width: 210 },
  { key: "process", label: "연결 공정", width: 130 },
  { key: "danger_category", label: "위험분류", width: 140 },
  { key: "danger_cause", label: "위험원인", width: 190 },
  { key: "danger_description", label: "위험요인 발생상황 및 결과" },
];

export default function GroupedCauseTable({
  data, query, renderToolbar, deleteMode, onDelete, recentRegisterCause,
}) {
  const [sorting, setSorting] = useState({ key: "machine", direction: "asc" });
  const groups = useMemo(() => buildCauseGroups(data, query, sorting), [data, query, sorting]);
  const visibleAssets = useMemo(() => groups.flatMap((group) => group.assets), [groups]);
  const recentAssets = new Set(recentRegisterCause?.assetIds || []);
  const recentCauses = new Set(recentRegisterCause?.dangerCauseIds || []);
  const columnCount = columns.length + (deleteMode ? 1 : 0);

  return (
    <Box className="safe-cause-grouped-table">
      <Box className="safe-cause-toolbar">{renderToolbar(visibleAssets)}</Box>
      <TableContainer className="safe-cause-table-scroll">
        <Table stickyHeader size="small" aria-label="설비별 위험요인 목록">
          <colgroup>
            {columns.map((column) => <col key={column.key} style={{ width: column.width }} />)}
            {deleteMode && <col style={{ width: 48 }} />}
          </colgroup>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} sortDirection={sorting.key === column.key ? sorting.direction : false}>
                  <TableSortLabel
                    active={sorting.key === column.key}
                    direction={sorting.key === column.key ? sorting.direction : "asc"}
                    onClick={() => setSorting((previous) => ({
                      key: column.key,
                      direction: previous.key === column.key && previous.direction === "asc" ? "desc" : "asc",
                    }))}
                  >{column.label}</TableSortLabel>
                </TableCell>
              ))}
              {deleteMode && <TableCell align="center">삭제</TableCell>}
            </TableRow>
          </TableHead>
          {groups.map((group) => (
            <TableBody key={group.key} className="safe-cause-machine-group">
              {group.assets.flatMap((item, assetIndex) => {
                const asset = item.registerAssetData;
                const situations = item.situationData.length ? item.situationData : [null];
                return situations.map((situation, situationIndex) => (
                  <TableRow
                    key={`${item.key}:${situation?.register_cause_id ?? situation?.danger_cause_id ?? "empty"}:${situationIndex}`}
                    className={situationIndex === 0 ? "safe-cause-process-start" : undefined}
                    sx={typeof item.__bgColor === "object" ? { "& > td": item.__bgColor } : undefined}
                  >
                    {assetIndex === 0 && situationIndex === 0 && (
                      <TableCell rowSpan={group.rowSpan} className="safe-cause-machine-cell">
                        <div className="safe-cause-machine-name">{getCauseMachineName(asset)}</div>
                        {asset.machine_name && asset.machine_name !== getCauseMachineName(asset) && (
                          <div className="safe-cause-machine-detail">{asset.machine_name}</div>
                        )}
                      </TableCell>
                    )}
                    {situationIndex === 0 && (
                      <TableCell rowSpan={item.rowSpan} className="safe-cause-process-cell">
                        <span>{asset.process_name || "공정 미등록"}</span>
                        {recentAssets.has(Number(asset.register_asset_id)) && <span className="safe-cause-new">NEW</span>}
                      </TableCell>
                    )}
                    {situation ? (
                      <>
                        <TableCell>{situation.danger_category || "-"}</TableCell>
                        <TableCell>{situation.danger_cause || "-"}</TableCell>
                        <TableCell>
                          {situation.danger_description || "위험요인 내용 미등록"}
                          {recentCauses.has(Number(situation.danger_cause_id)) && <span className="safe-cause-new">NEW</span>}
                        </TableCell>
                      </>
                    ) : (
                      <TableCell colSpan={3} className="safe-cause-empty-connection">연결된 유해·위험요인 없음</TableCell>
                    )}
                    {deleteMode && (
                      <TableCell align="center">
                        {situation && (
                          <Tooltip title="연결 삭제">
                            <IconButton
                              size="small" color="error"
                              aria-label={`${getCauseMachineName(asset)} · ${asset.process_name || "공정 미등록"} · ${situation.danger_description || "위험요인"} 연결 삭제`}
                              onClick={() => onDelete({ registerAssetData: asset, situationData: [situation] })}
                            ><DeleteOutlineIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ));
              })}
            </TableBody>
          ))}
          {!groups.length && (
            <TableBody><TableRow><TableCell colSpan={columnCount} className="safe-cause-empty">
              표시할 설비·위험요인이 없습니다.
            </TableCell></TableRow></TableBody>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
}
